/**
 * Flow D's data layer — the seven transaction screens' one door to the Worker.
 *
 * ── WHAT THIS REPLACED ───────────────────────────────────────────────────────
 * D1–D7 and H3 rendered `data.ts`: fixtures compiled into the bundle. Every amount was
 * formatted in the browser by `formatTaka`, every label was a constant beside the
 * component, and D4's confirm button built `৳ 1,190 দিয়ে নিশ্চিত করুন` out of a number
 * and a noun in JSX. The screens now render what the server sends and compose nothing.
 *
 * ── IT GOES THROUGH `api/client.ts`, WHICH IS THE ONLY PLACE FETCH IS CALLED ─
 * That module refuses three things on purpose, and every one of them is a rule this flow
 * depends on: it never composes Bangla, it never invents a state, and it never retries a
 * non-idempotent request. `POST /v1/orders` carries an `Idempotency-Key` this module
 * mints ONCE per checkout — see `orderIdempotencyKey` — so a double tap is deduped by
 * the server rather than by a transport that guessed.
 *
 * ── WHAT A FAILURE RENDERS ───────────────────────────────────────────────────
 * `Loaded` hands a designed failure to `states/boundary.tsx`, which maps the server's
 * code to one of the eleven states and renders the server's own `message_bn`. What it
 * deliberately does NOT do is invent a screen for a failure with no designed state: an
 * `UnmappedApiError` renders nothing at all, which is the honest outcome and the one
 * `docs/10 § WP-K` requires — there is no generic failure screen in this product.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  ApiErrorBody,
  BasketsResponse,
  CartResponse,
  CheckoutQuoteResponse,
  CompareResponse,
  CompareSort,
  OfferingResponse,
  OrderListResponse,
  OrderResponse,
} from '@oprappo/contracts';
import { api, ApiError, NetworkError } from '../../api/client.js';
import { SurfaceStates } from '../../states/boundary.js';

/* ---------------------------------------------------------------- the calls --- */

const path = (segment: string): string => encodeURIComponent(segment);

export const commerce = {
  offering: (id: string, signal?: AbortSignal): Promise<OfferingResponse> =>
    api.get<OfferingResponse>(`/v1/offerings/${path(id)}`, { signal }),

  compare: (id: string, sort: CompareSort | null, signal?: AbortSignal): Promise<CompareResponse> =>
    api.get<CompareResponse>(
      `/v1/offerings/${path(id)}/compare${sort === null ? '' : `?sort=${path(sort)}`}`,
      { signal },
    ),

  cart: (signal?: AbortSignal): Promise<CartResponse> => api.get<CartResponse>('/v1/cart', { signal }),

  addItem: (offeringId: string, quantity: number): Promise<CartResponse> =>
    api.post<CartResponse>('/v1/cart/items', { offering_id: offeringId, quantity }),

  addBasket: (basketId: string): Promise<CartResponse> =>
    api.post<CartResponse>('/v1/cart/basket', { basket_id: basketId }),

  setQuantity: (itemId: string, quantity: number): Promise<CartResponse> =>
    api.patch<CartResponse>(`/v1/cart/items/${path(itemId)}`, { quantity }),

  removeItem: (itemId: string): Promise<CartResponse> =>
    api.delete<CartResponse>(`/v1/cart/items/${path(itemId)}`),

  baskets: (categoryId: string, signal?: AbortSignal): Promise<BasketsResponse> =>
    api.get<BasketsResponse>(`/v1/baskets?category_id=${path(categoryId)}`, { signal }),

  /**
   * The full fee breakdown, BEFORE commitment. A POST because docs/05 says so: the quote
   * is computed from the caller's cart and this server's fee schedule, and the body is
   * empty because there is nothing about it a client may name.
   */
  quote: (signal?: AbortSignal): Promise<CheckoutQuoteResponse> =>
    api.post<CheckoutQuoteResponse>('/v1/checkout/quote', {}, { signal }),

  /**
   * The one call in this flow that moves money.
   *
   * `idempotencyKey` is REQUIRED by the route and is the caller's own — a double tap on
   * নিশ্চিত করুন sends the same key, and the server answers the first response instead of
   * placing a second order. Nothing below this line retries; `api/client.ts` says why.
   */
  placeOrder: (methodKey: string, idempotencyKey: string): Promise<OrderResponse> =>
    api.post<OrderResponse>('/v1/orders', { method_key: methodKey }, { idempotencyKey }),

  latestOrder: (signal?: AbortSignal): Promise<OrderListResponse> =>
    api.get<OrderListResponse>('/v1/orders?limit=1', { signal }),

  order: (reference: string, signal?: AbortSignal): Promise<OrderResponse> =>
    api.get<OrderResponse>(`/v1/orders/${path(reference)}`, { signal }),
} as const;

/**
 * One key per checkout attempt, minted before the request and reused by every retry of
 * the SAME intent.
 *
 * `crypto.randomUUID` is available in every browser this app supports and in the test
 * environment; the fallback exists because a checkout that throws while minting a key is
 * a checkout that cannot be completed at all.
 */
export function orderIdempotencyKey(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c !== undefined && typeof c.randomUUID === 'function') return c.randomUUID();
  return `order-${String(Date.now())}-${Math.random().toString(36).slice(2)}`;
}

/* ------------------------------------------------------------- the resource --- */

export type Resource<T> =
  | { status: 'pending' }
  | { status: 'ready'; data: T }
  /** `error` is null when the failure has no designed state — see `errorBodyOf`. */
  | { status: 'failed'; error: ApiErrorBody | null };

/**
 * An `ApiErrorBody` for a failure, or null when this client has no screen for it.
 *
 * Two of the three branches are decisions `api/client.ts` already made and this only
 * honours:
 *
 *   ApiError         the server named one of the nine codes. Its body travels unchanged,
 *                    including `message_bn` — the client never writes that sentence.
 *   NetworkError     "the request never left, or the answer never arrived. Maps to
 *                    E_OFFLINE's screen." `message_bn` is EMPTY, not composed: the drawn
 *                    ইন্টারনেট নেই frame carries its own copy.
 *   anything else    null. An `UnmappedApiError` is a state nobody designed, and mapping
 *                    it to a general failure screen is the one change that would quietly
 *                    undo docs/10 § WP-K.
 */
export function errorBodyOf(err: unknown): ApiErrorBody | null {
  if (err instanceof ApiError) return err.body;
  if (err instanceof NetworkError) {
    return {
      error: { code: 'E_OFFLINE', message_bn: '', message_en: err.message, reference: '' },
    };
  }
  return null;
}

/**
 * Load once per key, and expose a setter for the screens that mutate what they loaded.
 *
 * The setter is why this is not a one-line `useEffect`: D3's stepper and H3's basket
 * button both receive the WHOLE cart back from their write, and re-reading it afterwards
 * would show a stale footer in between.
 */
export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  key: string,
): [Resource<T>, (data: T) => void] {
  const [state, setState] = useState<Resource<T>>({ status: 'pending' });
  // The loader identity changes on every render; the KEY is what says whether this is a
  // different request. Held in a ref so the effect below depends on the key alone.
  const loader = useRef(load);
  loader.current = load;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setState({ status: 'pending' });
    loader
      .current(controller.signal)
      .then((data) => {
        if (live) setState({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        // An abort is this component unmounting, not a failure to show anybody.
        if (controller.signal.aborted) return;
        if (live) setState({ status: 'failed', error: errorBodyOf(err) });
      });
    return () => {
      live = false;
      controller.abort();
    };
  }, [key]);

  const set = useCallback((data: T) => {
    setState({ status: 'ready', data });
  }, []);

  return [state, set];
}

/**
 * Renders the screen when the data is there, the designed state when it is not, and
 * NOTHING while it is in flight.
 *
 * ── WHY `pending` DRAWS NOTHING ──────────────────────────────────────────────
 * The applicability matrix admits `loading` on the লেনদেন column, but the only loading
 * frame anybody drew is `P0 States § ৩`, which is B3's search-results skeleton — a
 * different screen, on the discovery surface. Rendering it here would be transcribing one
 * screen onto another, which is the substitution docs/01 exists to prevent, and inventing
 * a checkout skeleton would be worse. So nothing is drawn until there is something to
 * draw, and the gap is reported: the transaction surface needs a drawn skeleton.
 */
export function Loaded<T>({
  resource,
  children,
}: {
  resource: Resource<T>;
  children: (data: T) => ReactNode;
}): JSX.Element {
  if (resource.status === 'pending') return <></>;
  if (resource.status === 'failed') {
    // No designed state: render nothing rather than a screen nobody drew.
    if (resource.error === null) return <></>;
    return (
      <SurfaceStates surface="transaction" error={resource.error}>
        {null}
      </SurfaceStates>
    );
  }
  return <>{children(resource.data)}</>;
}
